import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { jobDownloadMutation } from '@quoruum/frontend-common/oas/gen/analytics/@tanstack/react-query.gen';
import {
  hotelGetOptions,
  hotelQueryOptions,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import {
  ANALYTICS_REPORT_TYPES,
  type AnalyticsReportType,
} from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import { Autocomplete } from '../components/Autocomplete';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';

type ReportType = AnalyticsReportType;
type HotelOption = {
  id: string;
  name: string;
  city?: string;
};

type InitialFormState = {
  type: ReportType;
  prefillHotelId: string;
  hotelSearch: string;
  selectedHotel: HotelOption | null;
  rateLatest: boolean;
  parquetNouns: boolean;
};

const HOTEL_SEARCH_DEBOUNCE_MS = 250;
const MIN_HOTEL_SEARCH_LENGTH = 2;

const REPORT_TYPES: ReadonlyArray<ReportType> = ANALYTICS_REPORT_TYPES;

function parseBooleanParam(value: string | null): boolean {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

function getApiErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const detail = (error as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  const errorText = (error as { error?: unknown }).error;
  if (typeof errorText === 'string' && errorText.trim()) {
    return errorText.trim();
  }

  return null;
}

function getInitialFormState(): InitialFormState {
  const searchParams = new URLSearchParams(window.location.search);

  const typeParam = searchParams.get('type');
  const hotelId = searchParams.get('hotelId')?.trim() ?? '';

  return {
    type: REPORT_TYPES.includes(typeParam as ReportType) ? (typeParam as ReportType) : 'RATE_COUNT',
    prefillHotelId: hotelId,
    hotelSearch: hotelId,
    selectedHotel: hotelId
      ? {
          id: hotelId,
          name: hotelId,
        }
      : null,
    rateLatest: parseBooleanParam(searchParams.get('rateLatest')),
    parquetNouns: parseBooleanParam(searchParams.get('parquetNouns')),
  };
}

export function Reports(): ReactElement {
  const initialFormState = useMemo(() => getInitialFormState(), []);

  const [type, setType] = useState<ReportType>(initialFormState.type);
  const [hotelSearch, setHotelSearch] = useState(initialFormState.hotelSearch);
  const [debouncedHotelSearch, setDebouncedHotelSearch] = useState(initialFormState.hotelSearch);
  const [selectedHotel, setSelectedHotel] = useState<HotelOption | null>(
    initialFormState.selectedHotel
  );
  const [rateLatest, setRateLatest] = useState(initialFormState.rateLatest);
  const [parquetNouns, setParquetNouns] = useState(initialFormState.parquetNouns);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: prefilledHotel } = useQuery({
    ...hotelGetOptions({
      path: {
        id: initialFormState.prefillHotelId,
      },
    }),
    enabled: initialFormState.prefillHotelId.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!prefilledHotel || !initialFormState.prefillHotelId) {
      return;
    }

    const hotelName = prefilledHotel.name ?? initialFormState.prefillHotelId;
    const hotelCity = prefilledHotel.city?.name;
    const displayLabel = `${hotelName}${hotelCity ? `, ${hotelCity}` : ''}`;

    setSelectedHotel((previousHotel) => {
      if (previousHotel?.id !== initialFormState.prefillHotelId) {
        return previousHotel;
      }

      return {
        id: initialFormState.prefillHotelId,
        name: hotelName,
        city: hotelCity,
      };
    });

    setHotelSearch((previousSearch) =>
      previousSearch === initialFormState.prefillHotelId ? displayLabel : previousSearch
    );
  }, [prefilledHotel, initialFormState.prefillHotelId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedHotelSearch(hotelSearch.trim());
    }, HOTEL_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hotelSearch]);

  const { data: hotelSearchResponse, isFetching: isHotelSearchFetching } = useQuery({
    ...(debouncedHotelSearch.length >= MIN_HOTEL_SEARCH_LENGTH
      ? hotelQueryOptions({
          query: {
            search: debouncedHotelSearch,
          },
        })
      : hotelQueryOptions()),
    enabled: debouncedHotelSearch.length >= MIN_HOTEL_SEARCH_LENGTH,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const hotelOptions = useMemo<HotelOption[]>(() => {
    const results = hotelSearchResponse?.results ?? [];
    return results
      .filter((hotel) => typeof hotel.id === 'string')
      .map((hotel) => ({
        id: hotel.id as string,
        name: hotel.name ?? 'Unknown Hotel',
        city: hotel.city?.name,
      }))
      .slice(0, 8);
  }, [hotelSearchResponse]);

  const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    void (async () => {
      try {
        const mutationOptions = jobDownloadMutation();
        const csv = await mutationOptions.mutationFn!(
          {
            query: {
              type,
            },
            body: {
              hotelId: selectedHotel?.id || undefined,
              rateLatest,
              parquetNouns,
            },
          },
          { client: queryClient, meta: undefined }
        );

        const blob = csv instanceof Blob ? csv : new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `report-${type.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } catch (submitError) {
        const apiErrorMessage = getApiErrorMessage(submitError);
        const message =
          apiErrorMessage ??
          (submitError instanceof Error ? submitError.message : 'Failed to download report.');
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    })();
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Reports</h1>
      <p className="mt-2 text-sm text-slate-600">Run a built-in report and download it as CSV.</p>

      <form className="mt-6 grid max-w-2xl gap-4" onSubmit={handleSubmit}>
        <label className="grid gap-1 text-sm font-medium text-slate-700">
          Report type
          <select
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-sky-500 focus:ring-2"
            value={type}
            onChange={(event) => setType(event.target.value as ReportType)}
          >
            {REPORT_TYPES.map((reportType) => (
              <option key={reportType} value={reportType}>
                {reportType}
              </option>
            ))}
          </select>
        </label>

        <div className="relative w-full max-w-md">
          <Autocomplete
            label="Hotel (optional)"
            value={hotelSearch}
            placeholder="Search hotel name or id"
            onValueChange={(value) => {
              setHotelSearch(value);
              setSelectedHotel(null);
            }}
            onEscapeKey={() => {
              setHotelSearch('');
              setSelectedHotel(null);
            }}
            options={selectedHotel ? [] : hotelOptions}
            minSearchLength={MIN_HOTEL_SEARCH_LENGTH}
            searchLength={debouncedHotelSearch.length}
            isLoading={isHotelSearchFetching}
            loadingLabel="Searching hotels"
            containerClassName="w-full max-w-md"
            getOptionKey={(hotel) => hotel.id}
            renderOption={({ option }) => (
              <>
                <span className="font-medium text-slate-900">{option.name}</span>
                <span className="ml-2 text-slate-500">
                  {option.city ? `${option.city} - ` : ''}
                  {option.id}
                </span>
              </>
            )}
            onSelectOption={(hotel) => {
              setSelectedHotel(hotel);
              setHotelSearch(`${hotel.name}${hotel.city ? `, ${hotel.city}` : ''}`);
            }}
          />
          {selectedHotel ? (
            <p className="mt-1 text-xs text-slate-500">Selected hotel id: {selectedHotel.id}</p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              Leave blank to run report without a hotel filter.
            </p>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            checked={rateLatest}
            onChange={(event) => setRateLatest(event.target.checked)}
          />
          Use latest rate (`rateLatest`)
        </label>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            checked={parquetNouns}
            onChange={(event) => setParquetNouns(event.target.checked)}
          />
          Read nouns from parquet (`parquetNouns`)
        </label>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-sky-400"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </section>
  );
}
